import Addon from '@shared/classes/addon';
import Channel from './channel';
import ChannelUtils from '../utils/channels';
import Content from '../types/enums/content';
import Country from '../types/interfaces/country';
import DateUtils from '../utils/date';
import Genre from './genre';
import IDate from '../types/interfaces/date';
import ImageUtils from '../utils/image';
import Plan from './plan';
import TranslationManager from '../assets/locale/translationsManager';
import { IEventSimplifiedRequest } from '../types/interfaces/event';
import { ParentalAge } from '../types/enums/parental';
import { RailType } from '../types/enums/rail';

class Event {
  ageCode?: ParentalAge;
  background?: string;
  categoryId?: string;
  catchup?: boolean | undefined;
  channel?: Channel;
  channelLogo: string | null;
  commercialScoreText: string | '' | undefined;
  country: string[];
  countries: Country[];
  crew: { [key: string]: string[] };
  date?: IDate;
  dummy?: boolean;
  episodeNumber: number | undefined;
  episodeTitle: string | undefined;
  eventId: string;
  programId: string | undefined;
  genreId:
    | string
    | { name: string; titles: { [key: string]: string } }
    | undefined;
  genreTitle: string | undefined;
  id: string;
  language?: string;
  mocked: boolean;
  photo: string;
  position?: number;
  poster: string;
  rail?: RailType;
  score?: number;
  serieId?: string;
  seriesTitle?: string;
  seasonNumber: number | undefined;
  synopsis: string;
  upgradablePlan: Plan | null;
  title: string;
  type: Content;
  year?: string | null;

  constructor(request: IEventSimplifiedRequest) {
    this.ageCode = request.ageCode;
    this.catchup = request?.catchup;
    this.categoryId = request?.category;
    this.channel = ChannelUtils.findChannel(request.channel);
    this.channelLogo =
      this?.channel?.logoWhite ||
      (this as unknown as Addon)?.logoWhite ||
      (this as unknown as Addon)?.logoColor ||
      null;
    this.country = request.country || [];
    this.countries = request.countries || [];
    this.dummy = request.dummy;
    this.episodeNumber = request.episode;
    this.episodeTitle = request.episodeTitle;
    this.eventId = request.eventId;
    this.programId = request.programId || undefined;
    this.genreId =
      (typeof request.gender === 'string'
        ? request.gender
        : request?.gender?.name) || '';
    this.id = request._id || request.id || '';
    this.language = request.language;
    this.mocked = request.mocked || false;
    this.rail = request.rail;
    this.score =
      request.score && request.score > 5 ? request.score / 2 : request.score;
    this.commercialScoreText = this.score ? this.score.toFixed(1) : '';
    this.position = request.position;
    this.serieId = request.serieId;
    this.seriesTitle = request.serie?.title || undefined;
    this.seasonNumber = request.season;
    this.title = request.title;
    this.type = request.type as Content;
    this.year = request.year || null;

    this.background = ImageUtils.getImage(
      request.moviePictures?.background || this.channel?.background || '',
      'background'
    );
    this.photo = ImageUtils.getImage(
      request.moviePictures?.photo || this.channel?.card || '',
      'photo'
    );
    this.poster = ImageUtils.getImage(
      request.moviePictures?.poster || this.channel?.poster || '',
      'poster'
    );

    this.crew = {
      actors: request.actors || [],
      hosts: request.hosts || [],
      directors: request.directors || [],
      writers: request.writers || [],
    };
    this.date =
      request.beginTime || request.endTime
        ? {
            start: new Date(request.beginTime).getTime(),
            end: new Date(request.endTime).getTime(),
          }
        : undefined;
    this.synopsis = (
      request.synopsisEpisode ||
      request.synopsisLong ||
      request.description ||
      request.longDescription ||
      request.synopsis ||
      ''
    ).replaceAll('<BR/>', '');

    this.upgradablePlan = ChannelUtils.getUpgradablePlan(
      this.channel,
      false,
      this.type
    );
  }

  getCountry() {
    if (this.countries.length) {
      return this.country[0];
    }
    return null;
  }

  getCountries(ellipsisWhenExceeding = false) {
    let result = '';
    let addEllipsis = false;
    const maxLength = 15;

    // Get countries from countries array
    if (this.countries.length) {
      result = this.countries?.reduce((prev, current) => {
        const country =
          typeof current === 'string'
            ? current
            : current?.localizable[TranslationManager.currentLanguage] || '';
        if (ellipsisWhenExceeding && prev && prev.length > maxLength) {
          addEllipsis = true;
          return prev;
        }
        return country && prev ? `${prev}, ${country}` : country;
      }, '');
    }

    // Else, get countries from country array
    if (!result && this.country.length) {
      result = this.country?.reduce((prev, current) => {
        if (ellipsisWhenExceeding && prev && prev.length > maxLength) {
          addEllipsis = true;
          return prev;
        }
        return prev ? `${prev}, ${current}` : current;
      }, '');
    }

    // Add ellipsis if needed
    if (addEllipsis) {
      result += '...';
    }

    // Return result
    return result;
  }

  getDuration() {
    if (this.date) {
      const duration = DateUtils.getDurationInMinutes(
        this.date.start,
        this.date.end
      );
      return duration ? duration + ' min' : 0;
    } else {
      return undefined;
    }
  }

  getEpisodeNumber() {
    return this.episodeNumber
      ? `${TranslationManager.get('MISC.EVENTS.EPISODE_FIRST_LETTER')}${
          this.episodeNumber
        }`
      : '';
  }

  getGenreTitle() {
    if (this.genreTitle) return this.genreTitle;
    else {
      if (typeof this.genreId === 'string') {
        return (this.genreTitle = Genre.getGenreById(
          this.genreId || ''
        )?.title);
      } else {
        const language = TranslationManager.currentLanguage;
        return (this.genreTitle = this.genreId?.titles[language]);
      }
    }
  }

  getSeasonNumber() {
    return this.seasonNumber
      ? `${TranslationManager.get('MISC.EVENTS.SEASON_FIRST_LETTER')}${
          this.seasonNumber
        }`
      : '';
  }

  isCurrentEvent() {
    const currentDate = Date.now();

    if (
      this.date &&
      currentDate > this.date.start &&
      currentDate < this.date.end
    ) {
      return true;
    }
    return false;
  }

  isFutureEvent() {
    return (this?.date?.start || 0) > new Date().getTime();
  }
}

export default Event;
